import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import UserSettingsService from "src/services/userSettingsService";

import { DeviceState } from "@dashboard/devices/types";
import {
    getDeviceBatteryHealthDesc,
    getDeviceBatteryIcon,
    getDeviceBatteryLevelText,
    getDeviceBatteryPresentDesc,
    getDeviceBatteryTemperatureDesc,
    getDeviceBatteryVoltageDesc,
    HardwareIconColor,
} from "@dashboard/shared/battery";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { getMemoryAlert, getMemoryUsageIcon, getMemoryUsageText } from "@dashboard/shared/memory";
import { Box, Typography } from "@mui/material";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

import { useStyles } from "./HardwareTab.styles";

const hexPrefix = "0x";
type SectionKey = "product" | "memory" | "battery" | "display" | "usb";

interface Props {
    deviceState: DeviceState;
}

function HardwareTab({ deviceState }: Props): React.JSX.Element {
    const { t } = useTranslation(["common", "sidepanels"]);
    const { classes } = useStyles();
    const { memory, battery, display, properties } = deviceState;
    const { usb = [] } = deviceState;

    const iconColorClassMap = {
        [HardwareIconColor.SECONDARY]: classes.iconNeutral,
        [HardwareIconColor.SUCCESS]: classes.iconPositive,
        [HardwareIconColor.WARNING]: classes.iconWarning,
        [HardwareIconColor.ERROR]: classes.iconNegative,
    };

    const batteryIcon = getDeviceBatteryIcon(battery);
    const batteryLevelText = getDeviceBatteryLevelText(battery);

    const memoryUsageIcon = getMemoryUsageIcon(memory);
    const memoryUsageText = getMemoryUsageText(memory);
    const memoryAlert = getMemoryAlert(memory);

    const savedExpandedSections = UserSettingsService.getLicenseSidePanelHardwareTabExpandedSections();
    const defaultExpandedSections: Record<SectionKey, boolean> = {
        product: true,
        memory: true,
        battery: true,
        display: true,
        usb: usb.length > 0,
    };

    const [expandedSections, setExpandedSections] = React.useState<Record<SectionKey, boolean>>(
        savedExpandedSections ? { ...defaultExpandedSections, ...savedExpandedSections } : defaultExpandedSections,
    );

    const handleSectionChange = (section: SectionKey) => (open: boolean) => {
        setExpandedSections((prev) => {
            const newState = { ...prev, [section]: open };
            UserSettingsService.setWorkspaceValue("licenseSidePanel.hardwareTab.expandedSections", newState);
            return newState;
        });
    };

    return (
        <PaneElement.Container>
            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.overviewTab.product")}</PaneElement.Header>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.hardwareProduct")}</PropertyKey>
                    <PropertyValue>{deviceState?.build?.product ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.model")}</PropertyKey>
                    <PropertyValue>{deviceState?.build?.model ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.manufacturer")}</PropertyKey>
                    <PropertyValue>{deviceState?.build?.manufacturer ?? "-"}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <CollapsibleProvider initialValue={expandedSections.memory} onChange={handleSectionChange("memory")}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle size="medium" bold>
                            {t("sidepanels:devicePanel.hardwareTab.memory")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.used")}</PropertyKey>
                            <PropertyValue>
                                <Box className={classes.valueWithIcon}>
                                    <i className={clsx(iconColorClassMap[memoryUsageIcon.color], "fa-solid", memoryUsageIcon.icon)} />
                                    <span>{memoryUsageText}</span>
                                </Box>
                            </PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("common:label.alert")}</PropertyKey>
                            <PropertyValue>
                                <Box className={classes.valueWithIcon}>
                                    <i className={clsx(iconColorClassMap[memoryAlert.color], "fa-solid", memoryAlert.icon)} />
                                    <span>
                                        {memoryAlert.isLow
                                            ? t("sidepanels:devicePanel.hardwareTab.lowMemoryReported")
                                            : t("sidepanels:devicePanel.hardwareTab.noAlerts")}
                                    </span>
                                </Box>
                            </PropertyValue>
                        </PropertyRow>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            </PaneElement.Section>

            <PaneElement.Section>
                <CollapsibleProvider initialValue={expandedSections.battery} onChange={handleSectionChange("battery")}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle size="medium" bold>
                            {t("sidepanels:devicePanel.hardwareTab.battery")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.level")}</PropertyKey>
                            <PropertyValue>
                                <Box className={classes.valueWithIcon}>
                                    <i className={clsx("fa-solid", batteryIcon.icon, iconColorClassMap[batteryIcon.color])} />
                                    <span>{batteryLevelText}</span>
                                </Box>
                            </PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.present")}</PropertyKey>
                            <PropertyValue>{getDeviceBatteryPresentDesc(battery)}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.voltage")}</PropertyKey>
                            <PropertyValue>{getDeviceBatteryVoltageDesc(battery)}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.temperature")}</PropertyKey>
                            <PropertyValue>{getDeviceBatteryTemperatureDesc(battery)}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.health")}</PropertyKey>
                            <PropertyValue>{getDeviceBatteryHealthDesc(battery)}</PropertyValue>
                        </PropertyRow>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            </PaneElement.Section>

            <PaneElement.Section>
                <CollapsibleProvider initialValue={expandedSections.display} onChange={handleSectionChange("display")}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle size="medium" bold>
                            {t("sidepanels:devicePanel.hardwareTab.display")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.name")}</PropertyKey>
                            <PropertyValue>{display?.name ?? "-"}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.id")}</PropertyKey>
                            <PropertyValue>{display?.id ?? "-"}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.size")}</PropertyKey>
                            <PropertyValue>{display?.size ?? "-"}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.realSize")}</PropertyKey>
                            <PropertyValue>{display?.realSize ?? "-"}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.rotation")}</PropertyKey>
                            <PropertyValue>{display?.rotation ?? "-"}</PropertyValue>
                        </PropertyRow>
                        <PropertyRow className={classes.propertyRow}>
                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.density")}</PropertyKey>
                            <PropertyValue>
                                {properties && properties["ro.sf.lcd_density"] ? properties["ro.sf.lcd_density"] : "-"}
                            </PropertyValue>
                        </PropertyRow>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            </PaneElement.Section>

            <PaneElement.Section withoutBorder>
                <CollapsibleProvider initialValue={expandedSections.usb} onChange={handleSectionChange("usb")}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle size="medium" bold>
                            {t("sidepanels:devicePanel.hardwareTab.usbDevices")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        {usb.length === 0 ? (
                            <PaneElement.Text>{t("sidepanels:devicePanel.hardwareTab.noUsbDevices")}</PaneElement.Text>
                        ) : (
                            <Box className={classes.usbDevicesList}>
                                {usb.map((item, i) => (
                                    <Box key={`${item?.path ?? i}`}>
                                        <Typography variant="subtitle2" className={classes.usbDeviceTitle}>
                                            {t("sidepanels:devicePanel.hardwareTab.usbDevice", { index: i + 1 })}
                                        </Typography>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.systemPath")}</PropertyKey>
                                            <PropertyValue>{item?.path ?? "-"}</PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.systemId")}</PropertyKey>
                                            <PropertyValue>{item?.number ?? "-"}</PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.productId")}</PropertyKey>
                                            <PropertyValue>
                                                {item?.productId ? `${hexPrefix}${item.productId}` : "-"}
                                            </PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.productName")}</PropertyKey>
                                            <PropertyValue>{item?.productName ?? "-"}</PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.vendorId")}</PropertyKey>
                                            <PropertyValue>{item?.vendorId ? `${hexPrefix}${item.vendorId}` : "-"}</PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.vendorName")}</PropertyKey>
                                            <PropertyValue>{item?.vendorName ?? "-"}</PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.serviceClass")}</PropertyKey>
                                            <PropertyValue>{item?.class ?? "-"}</PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.deviceSubclass")}</PropertyKey>
                                            <PropertyValue>{item?.subclass ?? "-"}</PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("common:label.serialNumber")}</PropertyKey>
                                            <PropertyValue>{item?.serial ?? "-"}</PropertyValue>
                                        </PropertyRow>
                                        <PropertyRow className={classes.propertyRow}>
                                            <PropertyKey>{t("sidepanels:devicePanel.hardwareTab.protocolType")}</PropertyKey>
                                            <PropertyValue>{item?.protocol ?? "-"}</PropertyValue>
                                        </PropertyRow>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            </PaneElement.Section>
        </PaneElement.Container>
    );
}

export default HardwareTab;
